// GIRegistration.jsx
import React from "react";

export default function GIRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Content */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Geographical Indication Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            GI Protection for Region‑Specific Products
          </h1>
          <div className="w-20 h-1 bg-amber-600 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks supports Geographical Indication Registration
            for region‑specific products, helping producer groups secure legal
            protection for names linked to distinctive local qualities.
          </p>
          <p className="text-gray-700">
            The focus is on building a clear technical and historical case that
            connects product reputation and characteristics with the claimed
            geographical area.
          </p>
        </div>

        {/* Right: Image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-amber-500 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/3735186/pexels-photo-3735186.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Local artisans and regional products representing geographical indication"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* DOCUMENTS & WHY CHOOSE US */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Documents Required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for GI Registration
            </h2>
            <p className="text-gray-700 mb-4">
              GI applications rely on detailed product and origin information so
              the registry can verify the claimed geographical connection.
            </p>
            <div className="bg-gray-50 border border-amber-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Product specification describing unique characteristics,
                  production methods, quality parameters, and how these depend
                  on local factors.
                </li>
                <li>
                  Proof of geographical origin, including maps of the region,
                  historical and documentary evidence of use, and information on
                  local producers or associations.
                </li>
                <li>
                  Applicant details such as constitution documents, address, and
                  authority of the association, organisation or government body
                  representing the producers.
                </li>
                <li>
                  Supporting materials like proposed inspection structure,
                  graphical representation of the GI, and any existing labels or
                  logos used in trade.
                </li>
              </ul>
            </div>
          </div>

          {/* Why Choose Hawkins & Brooks */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The service is designed to turn complex GI rules into clear,
              actionable steps for producer groups and local bodies.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-amber-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Intellectual Property Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Guidance on eligibility, drafting of the statement of case,
                  and positioning the product within the GI legal framework.
                </p>
              </div>

              <div className="border-l-4 border-amber-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Legal Documentation
                </h3>
                <p className="text-gray-700 text-sm">
                  Structured preparation of forms, annexures, maps, and
                  supporting evidence to reduce examination objections and
                  delays.
                </p>
              </div>

              <div className="border-l-4 border-amber-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Trusted Professional Assistance
                </h3>
                <p className="text-gray-700 text-sm">
                  Support through filing, examination, publication, opposition
                  response and post‑registration steps for authorised users.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
              >
                Get Consultation
              </a>
              <a
                href="tel:+919217468906"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-amber-600 text-amber-700 font-medium hover:bg-amber-50 transition-colors text-sm"
              >
                Call Hawkins &amp; Brooks
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
